import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, type Column } from "@legal-500/legal500-design-system";
import { DataFilters } from "@/components/DataFilters/DataFilters";
import type { FilterConfig } from "@/components/DataFilters/DataFilters";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { DATA_PRODUCTS_WRITE_PERMISSION } from "@/constants/permissions";
import { useQuery } from "@/hooks/useApi";
import { useFilters } from "@/hooks/useFilters";
import { usePermissions } from "@/hooks/usePermissions";
import { useUrlParams } from "@/hooks/useUrlParams";
import {
  HISTORICAL_DATA_REGION_IDS,
  dataProducts,
  type AdminDashboard,
  type DashboardKind,
} from "@/api/dataProducts.service";
import { historicalDataRegionLabel } from "@/pages/UserManagement/utils/historicalDataRegionLabel";
import { DashboardConfigurationBadge } from "./DashboardConfigurationWarning";
import { DashboardStatusChip } from "./DashboardStatusChip";
import {
  CATEGORY_LABELS,
  DASHBOARD_KIND_CONFIG,
  REQUIRED_PRODUCT_LABELS,
  STATUS_OPTIONS,
  compareEditions,
  formatEdition,
  isDashboardStatus,
} from "./dashboardKinds";
import styles from "./DataProducts.module.css";

const ALL_FILTER_VALUE = "all";

const STATUS_FILTER: FilterConfig = {
  key: "status",
  label: "Status",
  options: [{ value: ALL_FILTER_VALUE, label: "All statuses" }, ...STATUS_OPTIONS],
};

const FILTERS: FilterConfig[] = [STATUS_FILTER];
const FILTER_LABELS: Record<string, string> = { status: STATUS_FILTER.label };

type ListFilterValues = { status: string };

/** The URL is the filter state (as on Awards), so `useFilters`' mirror setter is a no-op. */
const IGNORE_FILTER_STATE: React.Dispatch<React.SetStateAction<ListFilterValues>> = () => undefined;

function formatUpdated(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB");
}

const REGION_ORDER: readonly string[] = HISTORICAL_DATA_REGION_IDS;

function regionRank(regionId: string | null): number {
  if (regionId === null) return REGION_ORDER.length + 1;
  const index = REGION_ORDER.indexOf(regionId);
  return index === -1 ? REGION_ORDER.length : index;
}

function sortByRegionThenEdition(rows: AdminDashboard[]): AdminDashboard[] {
  return [...rows].sort(
    (a, b) =>
      regionRank(a.region_id) - regionRank(b.region_id) ||
      (a.region_id ?? "").localeCompare(b.region_id ?? "") ||
      compareEditions(a.edition, b.edition) ||
      a.sort_order - b.sort_order ||
      a.title.localeCompare(b.title),
  );
}

function regionGroupStarts(rows: AdminDashboard[]): Set<string> {
  const starts = new Set<string>();
  rows.forEach((row, index) => {
    if (index > 0 && rows[index - 1]?.region_id !== row.region_id) starts.add(row.id);
  });
  return starts;
}

function categoryKey(row: AdminDashboard): string {
  return `${row.category}:${row.required_product}`;
}

function hasVaryingCategory(rows: AdminDashboard[]): boolean {
  return new Set(rows.map(categoryKey)).size > 1;
}

function buildColumns(kind: DashboardKind, showCategory: boolean): Column<AdminDashboard>[] {
  const columns: Column<AdminDashboard>[] = [
    {
      header: "Dashboard",
      accessor: "title",
      render: (_value, row) => (
        <div
          className={styles.titleCell}
          title={row.description ? `${row.title}\n${row.description}` : row.title}
        >
          <div className={styles.titleLine}>
            <span className={styles.rowTitle}>{row.title}</span>
            <code className={styles.slug}>{row.slug}</code>
          </div>
          {row.description && <span className={styles.rowDescription}>{row.description}</span>}
        </div>
      ),
    },
  ];

  if (kind === "tableau") {
    columns.push(
      {
        header: "Region",
        accessor: "region_id",
        render: (_value, row) => (row.region_id ? historicalDataRegionLabel(row.region_id) : "—"),
      },
      {
        header: "Edition",
        accessor: "edition",
        render: (_value, row) => formatEdition(row.edition),
      },
    );
  }

  if (showCategory) {
    columns.push({
      header: "Category",
      accessor: "category",
      render: (_value, row) => (
        <span>
          {CATEGORY_LABELS[row.category]}
          <span className={styles.mutedInline}>
            {REQUIRED_PRODUCT_LABELS[row.required_product]}
          </span>
        </span>
      ),
    });
  }

  columns.push(
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <div className={styles.statusCell}>
          <DashboardStatusChip status={row.status} />
          <DashboardConfigurationBadge issues={row.configurationIssues} />
        </div>
      ),
    },
    {
      header: "Updated",
      accessor: "updated_at",
      render: (_value, row) => (
        <span className={styles.updated}>{formatUpdated(row.updated_at)}</span>
      ),
    },
  );

  return columns;
}

export type DashboardListProps = {
  kind: DashboardKind;
};

export function DashboardList({ kind }: DashboardListProps) {
  const config = DASHBOARD_KIND_CONFIG[kind];
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(DATA_PRODUCTS_WRITE_PERMISSION);

  const { searchParams, updateUrl } = useUrlParams();
  const filters = useMemo<ListFilterValues>(
    () => ({ status: searchParams.get("status") || ALL_FILTER_VALUE }),
    [searchParams],
  );
  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters: IGNORE_FILTER_STATE,
    updateUrl,
    labelOverrides: FILTER_LABELS,
  });

  const status = isDashboardStatus(filters.status) ? filters.status : undefined;

  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => dataProducts.list({ kind, status }),
    [kind, status],
  );

  const rows = useMemo(() => {
    const dashboards = data?.dashboards ?? [];
    return kind === "tableau" ? sortByRegionThenEdition(dashboards) : dashboards;
  }, [data, kind]);
  const groupStarts = useMemo(
    () => (kind === "tableau" ? regionGroupStarts(rows) : new Set<string>()),
    [kind, rows],
  );
  const showCategory = hasVaryingCategory(rows);
  const columns = useMemo(() => buildColumns(kind, showCategory), [kind, showCategory]);

  const enhancedActiveFilters = useMemo(
    () =>
      activeFilters.map(filter => ({
        ...filter,
        displayValue:
          STATUS_FILTER.options.find(option => String(option.value) === String(filter.value))
            ?.label ?? String(filter.value),
      })),
    [activeFilters],
  );

  const errorStatus = (error as (Error & { status?: number }) | null)?.status;
  const isAuthError = errorStatus === 401 || errorStatus === 403;
  const hasResolvedRows = !loading && !isRefetching && !error && rows.length > 0;

  const renderListContent = () => {
    if (loading || isRefetching) {
      return <LoadingState message="Loading dashboards..." />;
    }
    if (isAuthError) {
      return <UnauthorizedView />;
    }
    if (error) {
      return (
        <EmptyState
          variant="error"
          title="Failed to load dashboards"
          message={error.message}
          action={{ label: "Retry", onClick: () => refetch() }}
        />
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          variant="empty"
          title="No dashboards found"
          message={
            status
              ? "No dashboards match this status."
              : `No ${config.singular.toLowerCase()}s yet.`
          }
        />
      );
    }
    return (
      <DataTable
        data={rows}
        columns={columns}
        rowKey="id"
        className={styles.dashboardTable}
        rowClassName={row => (groupStarts.has(row.id) ? styles.groupStart : "")}
        onRowClick={row => navigate(config.detailRoute(row.id))}
      />
    );
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader title={config.title}>
        {canWrite && (
          <div className={styles.headerActions}>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={() => navigate(config.newRoute)}
            >
              New dashboard
            </Button>
          </div>
        )}
      </PageHeader>

      <div className={styles.filters}>
        <DataFilters
          filters={FILTERS}
          values={filters}
          onChange={(key, value) => handleFilterChange(key, value)}
          activeFilters={enhancedActiveFilters}
          onClearFilter={key => handleClearFilter(key)}
        >
          {hasResolvedRows && (
            <span className={styles.resultCount}>
              {rows.length} dashboard{rows.length === 1 ? "" : "s"}
            </span>
          )}
        </DataFilters>
      </div>

      {renderListContent()}
    </div>
  );
}

export default DashboardList;
